import { useCallback, useEffect, useRef, useState } from 'react';

import { ancanaApi } from '../../../api/ancanaApi';
// A generic axios-error reader that happens to live in the tenancy folder.
// Imported rather than copied so both platform-ops pages report failures the
// same way.
import { errorMessage } from '../tenancy/useOperators';
import { IIndexHealth } from '../../../types/models/pathfinder';

const PATH = 'pathfinder/health/';

/** How often the page re-reads the index's state, unprompted. */
export const REFRESH_MS = 60_000;

export interface UseIndexHealthResult {
  health?: IIndexHealth;
  isLoading: boolean;
  /** Set only when there is no report to show at all — a 403, or a network failure. */
  error?: string;
  isForbidden: boolean;
  reload: () => Promise<void>;
}

/**
 * Pathfinder's index health.
 *
 * The one subtle thing here: **a 503 is a successful read.** ancana_api
 * deliberately answers 503 when the index is degraded or unreadable, and puts
 * the full report in the body — invisible staleness is the failure this whole
 * chain exists to prevent, so the honest status code is the point. axios treats
 * any 503 as a thrown error, so a hook that only looked at the happy path would
 * turn "degraded, here is why" into "could not load", which is the same lie in
 * a different shape.
 *
 * A body carrying `status` is therefore the answer regardless of status code.
 * `error` is reserved for having no report at all.
 */
const useIndexHealth = (): UseIndexHealthResult => {
  const [health, setHealth] = useState<IIndexHealth>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [isForbidden, setIsForbidden] = useState(false);
  // Guards against a slow in-flight read landing after the component unmounts.
  const mounted = useRef(true);

  const reload = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await ancanaApi.get(PATH);
      if (!mounted.current) return;
      setHealth(response.data);
      setError(undefined);
      setIsForbidden(false);
    } catch (e: any) {
      if (!mounted.current) return;
      const status = e?.response?.status;
      const data = e?.response?.data;

      if (data?.status) {
        // 503 with a report: degraded or unavailable, and the body says which.
        setHealth(data);
        setError(undefined);
        setIsForbidden(false);
      } else {
        setHealth(undefined);
        setIsForbidden(status === 403);
        setError(errorMessage(e, 'Could not read the index status.'));
      }
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    reload();
    const timer = setInterval(reload, REFRESH_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [reload]);

  return { health, isLoading, error, isForbidden, reload };
};

export default useIndexHealth;
